import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import Modal from 'components/Layout/Modal';
import ElevatedButton from 'components/Layout/ElevatedButton';
import api, { API_ENDPOINTS } from 'api/config';
import { parseFlashcardImportText } from 'utils/flashcardImport';

const Tabs = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
`;

const TabButton = styled.button`
  border: 1px solid ${props => props.theme.border || 'rgba(255,255,255,0.12)'};
  background: ${props => (props.$active ? props.theme.buttonPrimary : props.theme.backgroundSecondary)};
  color: ${props => (props.$active ? '#fff' : props.theme.text)};
  border-radius: 999px;
  padding: 0.65rem 1rem;
  font-weight: 600;
  cursor: pointer;
`;

const FormGrid = styled.div`
  display: grid;
  gap: 1rem;
`;

const FieldLabel = styled.label`
  display: grid;
  gap: 0.4rem;
  color: ${props => props.theme.textSecondary || props.theme.text};
  font-weight: 600;
  font-size: 0.92rem;
`;

const Input = styled.input`
  border: 1px solid ${props => props.theme.border || 'rgba(255,255,255,0.12)'};
  background: ${props => props.theme.backgroundSecondary};
  color: ${props => props.theme.text};
  border-radius: 10px;
  padding: 0.8rem 0.9rem;
  width: 100%;
`;

const TextArea = styled.textarea`
  border: 1px solid ${props => props.theme.border || 'rgba(255,255,255,0.12)'};
  background: ${props => props.theme.backgroundSecondary};
  color: ${props => props.theme.text};
  border-radius: 10px;
  padding: 0.8rem 0.9rem;
  width: 100%;
  min-height: 140px;
  resize: vertical;
`;

const HelperText = styled.p`
  margin: 0;
  font-size: 0.85rem;
  color: ${props => props.theme.textMuted};
`;

const cardsToText = (cards) => cards
  .map((card) => {
    const front = typeof card?.front === 'string' ? card.front.replace(/"/g, '""') : '';
    const back = typeof card?.back === 'string' ? card.back.replace(/"/g, '""') : '';
    return `"${front}"\t"${back}"`;
  })
  .join('\n');

const CardRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr auto;
  gap: 0.75rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const CardActions = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 0.5rem;
`;

const SecondaryButton = styled.button`
  border: 1px solid ${props => props.theme.border || 'rgba(255,255,255,0.12)'};
  background: ${props => props.theme.backgroundSecondary};
  color: ${props => props.theme.text};
  border-radius: 10px;
  padding: 0.8rem 1rem;
  font-weight: 600;
  cursor: pointer;
`;

const DangerButton = styled.button`
  border: 1px solid rgba(239, 68, 68, 0.45);
  background: rgba(239, 68, 68, 0.1);
  color: #ef4444;
  border-radius: 10px;
  padding: 0.8rem 1rem;
  font-weight: 600;
  cursor: pointer;
`;

const DeckEditorModal = ({
  isOpen,
  onClose,
  onSave,
  courses = [],
  initialCourseId = '',
  isPremium = false,
  usage = { decks: 0, cards: 0 },
  limits = { deckLimit: 3, cardLimit: 250 },
  saving = false
}) => {
  const [mode, setMode] = useState('manual');
  const [title, setTitle] = useState('');
  const [courseId, setCourseId] = useState(initialCourseId || '');
  const [sourceName, setSourceName] = useState('anki');
  const [importText, setImportText] = useState('');
  const [cards, setCards] = useState([{ cardId: 'card-1', front: '', back: '', tags: [] }]);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    setTitle('');
    setCourseId(initialCourseId || '');
    setSourceName('anki');
    setImportText('');
    setCards([{ cardId: 'card-1', front: '', back: '', tags: [] }]);
    setError('');
    setMode('manual');
  }, [initialCourseId, isOpen]);

  const importPreview = useMemo(() => parseFlashcardImportText(importText), [importText]);

  const updateCard = (index, field, value) => {
    setCards((currentCards) => currentCards.map((card, cardIndex) => (
      cardIndex === index ? { ...card, [field]: value } : card
    )));
  };

  const addCard = () => {
    setCards((currentCards) => ([
      ...currentCards,
      {
        cardId: `card-${currentCards.length + 1}`,
        front: '',
        back: '',
        tags: []
      }
    ]));
  };

  const removeCard = (index) => {
    setCards((currentCards) => {
      if (currentCards.length === 1) {
        return currentCards;
      }

      return currentCards.filter((_, cardIndex) => cardIndex !== index);
    });
  };

  const handleSave = async () => {
    setError('');

    if (!title.trim()) {
      setError('Add a deck title before saving.');
      return;
    }

    const normalizedCourseId = courseId || null;

    if (mode === 'manual') {
      const validCards = cards
        .map((card, index) => ({
          cardId: card.cardId || `card-${index + 1}`,
          front: card.front.trim(),
          back: card.back.trim(),
          tags: Array.isArray(card.tags) ? card.tags : []
        }))
        .filter((card) => card.front && card.back);

      if (!validCards.length) {
        setError('Add at least one complete flashcard with a front and a back.');
        return;
      }

      try {
        await onSave({
          title: title.trim(),
          courseId: normalizedCourseId,
          sourceType: 'manual',
          sourceName: null,
          cards: validCards
        });
      } catch (saveError) {
        setError(saveError.message || 'Failed to save flashcard deck.');
      }
      return;
    }

    const parsedCards = parseFlashcardImportText(importText);
    if (!parsedCards.length) {
      setError('Paste or upload flashcards in CSV, TSV, or simple front/back format.');
      return;
    }

    try {
      await onSave({
        title: title.trim(),
        courseId: normalizedCourseId,
        sourceType: sourceName,
        sourceName,
        cards: parsedCards
      });
    } catch (saveError) {
      setError(saveError.message || 'Failed to import flashcard deck.');
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    setError('');

    if (/\.apkg$/i.test(file.name)) {
      const importApkg = async () => {
        try {
          const formData = new FormData();
          formData.append('file', file);

          const response = await api.post(API_ENDPOINTS.FLASHCARD_DECKS_IMPORT_ANKI, formData);

          const importedCards = Array.isArray(response.data.cards) ? response.data.cards : [];
          setImportText(cardsToText(importedCards));
        } catch (uploadError) {
          setError(
            uploadError?.response?.data?.message ||
            uploadError?.response?.data?.error ||
            uploadError?.message ||
            'Failed to extract flashcards from the Anki deck.'
          );
        }
      };

      importApkg();
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setImportText(typeof reader.result === 'string' ? reader.result : '');
    };
    reader.readAsText(file);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Create or Import Flashcards" size="xlarge">
      <FormGrid>
        <Tabs>
          <TabButton type="button" $active={mode === 'manual'} onClick={() => setMode('manual')}>
            Make cards manually
          </TabButton>
          <TabButton type="button" $active={mode === 'import'} onClick={() => setMode('import')}>
            Import from Anki, CSV, or TSV
          </TabButton>
        </Tabs>

        <HelperText>
          Free accounts can save up to {limits.deckLimit} decks and {limits.cardLimit} flashcards total. You currently have {usage.decks} decks and {usage.cards} cards saved.
          {isPremium ? ' Premium access removes these limits.' : ' Upgrade for unlimited decks and cards.'}
        </HelperText>

        <FieldLabel>
          Deck title
          <Input value={title} onChange={(event) => setTitle(event.target.value)} placeholder="e.g. Chapter 3 - Cell Biology" />
        </FieldLabel>

        <FieldLabel>
          Course link (optional)
          <select value={courseId} onChange={(event) => setCourseId(event.target.value)} style={{ width: '100%', padding: '0.8rem 0.9rem', borderRadius: '10px' }}>
            <option value="">No course</option>
            {courses.map((course) => (
              <option key={course._id} value={course._id}>
                {course.course_code} - {course.name}
              </option>
            ))}
          </select>
        </FieldLabel>

        {mode === 'manual' ? (
          <>
            {cards.map((card, index) => (
              <CardRow key={card.cardId}>
                <FieldLabel>
                  Front
                  <TextArea
                    value={card.front}
                    onChange={(event) => updateCard(index, 'front', event.target.value)}
                    placeholder="Question or prompt"
                  />
                </FieldLabel>
                <FieldLabel>
                  Back
                  <TextArea
                    value={card.back}
                    onChange={(event) => updateCard(index, 'back', event.target.value)}
                    placeholder="Answer or explanation"
                  />
                </FieldLabel>
                <CardActions>
                  <DangerButton type="button" onClick={() => removeCard(index)} disabled={cards.length === 1}>
                    Remove
                  </DangerButton>
                </CardActions>
              </CardRow>
            ))}

            <SecondaryButton type="button" onClick={addCard}>
              Add another card
            </SecondaryButton>
          </>
        ) : (
          <>
            <FieldLabel>
              Import source
              <select value={sourceName} onChange={(event) => setSourceName(event.target.value)} style={{ width: '100%', padding: '0.8rem 0.9rem', borderRadius: '10px' }}>
                <option value="anki">Anki export</option>
                <option value="csv">CSV export</option>
                <option value="tsv">TSV export</option>
              </select>
            </FieldLabel>

            <FieldLabel>
              Upload a file - Currently support .txt, .csv, .json, and .tsv file formats. Anki .apkg files are not currently supported.
              <Input type="file" accept=".csv,.tsv,.txt,.json" onChange={handleFileChange} />
            </FieldLabel>

            <FieldLabel>
              Paste flashcards
              <TextArea
                value={importText}
                onChange={(event) => setImportText(event.target.value)}
                placeholder="Front<TAB>Back\nQuestion 1<TAB>Answer 1"
              />
            </FieldLabel>

            <HelperText>
              Upload a full Anki .apkg deck or paste a text export. Uploaded .apkg files are extracted before import.
            </HelperText>

            <HelperText>
              Parsed preview: {importPreview.length} flashcard{importPreview.length === 1 ? '' : 's'} ready to import.
            </HelperText>
          </>
        )}

        {error && <HelperText style={{ color: '#ef4444' }}>{error}</HelperText>}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', flexWrap: 'wrap' }}>
          <SecondaryButton type="button" onClick={onClose} disabled={saving}>
            Cancel
          </SecondaryButton>
          <ElevatedButton type="button" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : mode === 'manual' ? 'Save deck' : 'Import deck'}
          </ElevatedButton>
        </div>
      </FormGrid>
    </Modal>
  );
};

export default DeckEditorModal;